import { arrayMove } from '@dnd-kit/sortable'
import clamp from 'lodash-es/clamp'
import range from 'lodash-es/range'
import sumBy from 'lodash-es/sumBy'

import { isValueObjectType } from '../../../utils/common'
import { checkIsBirdviewLoadingState } from '../DashboardBirdview.utils.sections'

import {
  BIRDVIEW_CHART_GRID_COLUMN_GAP,
  BIRDVIEW_CHART_GRID_GAP,
  BIRDVIEW_CHART_GRID_MIN_COLUMN_SPAN,
  BIRDVIEW_CHART_GRID_MIN_ROW_SPAN,
  BIRDVIEW_CHART_GRID_MIN_TRACKS,
  BIRDVIEW_CHART_GRID_ROW_GAP,
  BIRDVIEW_CHART_GRID_ROW_HEIGHT,
  BIRDVIEW_CHART_GRID_SPAN_BOUNDS,
  BIRDVIEW_CHART_GRID_SPAN_STEP,
  BirdviewChartGridAxis,
} from './BirdviewChartGrid.constants'

function getBirdviewChartGridMeasuredSpan({ gap, size, step }) {
  return Math.max(
    BIRDVIEW_CHART_GRID_MIN_TRACKS,
    Math.round((size + gap) / step)
  )
}

// Read off the measured cells, so the preview stays right when a container
// query collapses the columns.
function getBirdviewChartGridTracks(rects) {
  const trackWidth = Math.min(...rects.map(rect => rect.width))
  const left = Math.min(...rects.map(rect => rect.left))
  const right = Math.max(...rects.map(rect => rect.right))
  const columnStep = trackWidth + BIRDVIEW_CHART_GRID_COLUMN_GAP

  return {
    columns: getBirdviewChartGridMeasuredSpan({
      gap: BIRDVIEW_CHART_GRID_COLUMN_GAP,
      size: right - left,
      step: columnStep,
    }),
    columnStep,
    rowStep: BIRDVIEW_CHART_GRID_ROW_HEIGHT + BIRDVIEW_CHART_GRID_ROW_GAP,
  }
}

function getBirdviewChartGridMeasuredSpans(rects, tracks) {
  return rects.map(rect => ({
    columns: Math.min(
      tracks.columns,
      getBirdviewChartGridMeasuredSpan({
        gap: BIRDVIEW_CHART_GRID_COLUMN_GAP,
        size: rect.width,
        step: tracks.columnStep,
      })
    ),
    rows: getBirdviewChartGridMeasuredSpan({
      gap: BIRDVIEW_CHART_GRID_ROW_GAP,
      size: rect.height,
      step: tracks.rowStep,
    }),
  }))
}

function getBirdviewChartGridCellKey(row, column) {
  return `${row}:${column}`
}

function getBirdviewChartGridAreaKeys({ column, row, span }) {
  return range(span.rows).flatMap(rowOffset =>
    range(span.columns).map(columnOffset =>
      getBirdviewChartGridCellKey(row + rowOffset, column + columnOffset)
    )
  )
}

function checkIsBirdviewChartGridAreaFree({ taken, ...area }) {
  return getBirdviewChartGridAreaKeys(area).every(key => !taken.has(key))
}

function takeBirdviewChartGridArea({ taken, ...area }) {
  getBirdviewChartGridAreaKeys(area).forEach(key => taken.add(key))
}

// Candidate cells in the order `grid-auto-flow: row dense` tries them.
function getBirdviewChartGridCells({ columns, rows, span }) {
  return range(rows).flatMap(row =>
    range(columns - span.columns + 1).map(column => ({ column, row }))
  )
}

function findBirdviewChartGridFreeCell({ columns, rows, span, taken }) {
  return getBirdviewChartGridCells({ columns, rows, span }).find(cell =>
    checkIsBirdviewChartGridAreaFree({ ...cell, span, taken })
  )
}

// Stacked one under another the items need the summed rows at most.
function placeBirdviewChartGridItems({ columns, spans }) {
  const rows = sumBy(spans, 'rows')
  const taken = new Set()

  return spans.map(span => {
    const cell = findBirdviewChartGridFreeCell({ columns, rows, span, taken })

    takeBirdviewChartGridArea({ ...cell, span, taken })

    return cell
  })
}

export function getBirdviewChartGridItemId(item) {
  return item.id
}

export function checkIsBirdviewChartGridSpans(value) {
  return isValueObjectType(value) && !Array.isArray(value)
}

export function checkIsBirdviewChartGridItemLocked(item) {
  return checkIsBirdviewLoadingState(item.section?.state)
}

export function checkIsBirdviewChartGridItemShown({ hiddenIds, item }) {
  return Boolean(item) && !hiddenIds.includes(getBirdviewChartGridItemId(item))
}

export function checkIsBirdviewChartGridAddCardShown({ rows }) {
  return rows.length > 0
}

export function getBirdviewChartGridNextColumnSpan(span) {
  const { max, min } =
    BIRDVIEW_CHART_GRID_SPAN_BOUNDS[BirdviewChartGridAxis.Column]

  return span >= max
    ? min
    : span + BIRDVIEW_CHART_GRID_SPAN_STEP[BirdviewChartGridAxis.Column]
}

export function checkIsBirdviewChartGridColumnAxis(axis) {
  return axis === BirdviewChartGridAxis.Column
}

export function getBirdviewChartGridDefaultColumnSpans(items) {
  return Object.fromEntries(
    items.map(item => [
      getBirdviewChartGridItemId(item),
      item.columnSpan ?? BIRDVIEW_CHART_GRID_MIN_COLUMN_SPAN,
    ])
  )
}

export function getBirdviewChartGridDefaultRowSpans(items) {
  return Object.fromEntries(
    items.map(item => [
      getBirdviewChartGridItemId(item),
      BIRDVIEW_CHART_GRID_MIN_ROW_SPAN,
    ])
  )
}

export function clampBirdviewChartGridSpan({ axis, span }) {
  const { max, min } = BIRDVIEW_CHART_GRID_SPAN_BOUNDS[axis]

  return clamp(span, min, max)
}

export function getBirdviewChartGridStep({ axis, size, span }) {
  return span > 0 ? (size + BIRDVIEW_CHART_GRID_GAP[axis]) / span : size
}

export function getBirdviewChartGridResizedSpan({ axis, delta, span, step }) {
  const spanStep = BIRDVIEW_CHART_GRID_SPAN_STEP[axis]

  return clampBirdviewChartGridSpan({
    axis,
    span: span + Math.round(delta / (step * spanStep)) * spanStep,
  })
}

export function getBirdviewChartGridSpanStyle({ columnSpan, rowSpan }) {
  return {
    '--birdview-chart-column-span': columnSpan,
    '--birdview-chart-row-span': rowSpan,
  }
}

// The stock rect strategy scales cards to their neighbours' sizes; this moves
// each card to the cell the dense layout will really give it.
export function sortBirdviewChartGridItems({
  activeIndex,
  index,
  overIndex,
  rects,
}) {
  if (rects.some(rect => !rect)) {
    return null
  }

  const tracks = getBirdviewChartGridTracks(rects)
  const spans = getBirdviewChartGridMeasuredSpans(rects, tracks)
  const movedIndexes = arrayMove(
    spans.map((_, spanIndex) => spanIndex),
    activeIndex,
    overIndex
  )

  const before = placeBirdviewChartGridItems({ columns: tracks.columns, spans })
  const after = placeBirdviewChartGridItems({
    columns: tracks.columns,
    spans: movedIndexes.map(spanIndex => spans[spanIndex]),
  })

  const from = before[index]
  const to = after[movedIndexes.indexOf(index)]

  return {
    scaleX: 1,
    scaleY: 1,
    x: (to.column - from.column) * tracks.columnStep,
    y: (to.row - from.row) * tracks.rowStep,
  }
}
